import type React from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog"
import { ScrollArea } from "@/components/ui/scroll-area"

interface AboutUsPopupProps {
  isOpen: boolean
  onClose: () => void
}

const AboutUsPopup: React.FC<AboutUsPopupProps> = ({ isOpen, onClose }) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>About Coverage Maps</DialogTitle>
          <DialogDescription>Last updated: February 2025</DialogDescription>
        </DialogHeader>
        <ScrollArea className="mt-4 max-h-[60vh] pr-4">
          <div className="space-y-4 text-sm">
            <p>
              Coverage Maps is a powerful, free tool designed to help businesses visualize and manage their territories
              effectively. Whether you're managing sales regions, service areas, or franchise territories, our platform
              provides the tools you need to make informed decisions.
            </p>

            <h3 className="font-semibold text-base">Our Mission</h3>
            <p>
              We believe that effective territory management should be accessible to everyone. Our mission is to provide
              a free, user-friendly platform that helps businesses of all sizes optimize their geographic coverage and
              improve their operational efficiency.
            </p>

            <h3 className="font-semibold text-base">Key Features</h3>
            <ul className="list-disc pl-5 space-y-2">
              <li>Interactive territory mapping for US and worldwide regions</li>
              <li>Easy territory assignment and management</li>
              <li>Export capabilities (PDF, PNG, CSV)</li>
              <li>Collaborative team features</li>
              <li>User-friendly interface</li>
            </ul>

            <h3 className="font-semibold text-base">Why Choose Coverage Maps?</h3>
            <ul className="list-disc pl-5 space-y-2">
              <li>Free to use with no hidden costs</li>
              <li>Intuitive drag-and-drop interface</li>
              <li>Comprehensive territory management tools</li>
              <li>Regular updates and new features</li>
              <li>Responsive customer support</li>
            </ul>

            <h3 className="font-semibold text-base">Our Commitment</h3>
            <p>
              We are committed to providing a reliable, secure, and continuously improving platform for all your
              territory mapping needs. Our team regularly updates the platform with new features and improvements based
              on user feedback.
            </p>

            <h3 className="font-semibold text-base">Contact Us</h3>
            <p>
              Have questions or suggestions? We'd love to hear from you. Contact our team at{" "}
              <a href="mailto:contact@coverage-maps.com" className="text-blue-600 hover:underline">
                contact@coverage-maps.com
              </a>
              .
            </p>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  )
}

export default AboutUsPopup
